import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { useAuthStore } from '@/store/useAuthStore'
import { ResponseData } from './types'
import { message } from 'antd'

class HttpRequest {
  // axios 实例
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: this.getBaseURL(),
      timeout: 10000, // 10秒超时
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  // 获取基础URL
  public getBaseURL(): string {
    if (import.meta.env.MODE === 'production') {
      return '/'

    }
    return 'http://localhost:8000'

  }

  // 获取完整的头像URL
  public getAvatarUrl(path: string): string {

    if (import.meta.env.MODE === 'development') {
      return `${this.getBaseURL()}/data${path}`;
    }
    return path;
  }

  // 获取可对外使用的媒体文件 URL。
  //
  // 约定：后端返回的是根相对路径（如 "public/upload/video/x.mp4"，历史上有带/不带前导斜杠两种），
  // 该地址会被写进文章封面/正文，因此**统一返回根相对路径**——绝对地址（http://localhost:8000）
  // 会把开发机地址固化进内容，发到线上就是死链。
  // 注意必须补前导斜杠：否则会拼成 http://localhost:8000public/... 直接 404。
  public getMediaUrl(path: string): string {
    if (!path) return '';
    return path.startsWith('/') ? path : `/${path}`;
  }

  // 从 cookie 中读取 CSRF token
  private getCsrfToken(): string | null {
    const match = document.cookie.match(/csrftoken=([^;]+)/);
    return match ? match[1] : null;
  }

  // 设置拦截器
  private setupInterceptors(): void {
    // 请求拦截器
    this.instance.interceptors.request.use(
      (config) => {
        const token = useAuthStore.getState().token
        if (token) {
          config.headers.Authorization = `Token ${token}`
        }
        // 非 GET 请求添加 CSRF Token（Django SessionAuthentication 需要）
        if (config.method !== 'get') {
          const csrfToken = this.getCsrfToken()
          if (csrfToken) {
            config.headers['X-CSRFToken'] = csrfToken
          }
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response
      },
      (error) => {
        // if (error.response?.status === 401) {
        //   useAuthStore.getState().clearAuth()
        //   window.location.href = '/login'
        // }
        // 榜单接口 403（post 模式比赛进行中，非管理员请求）为预期行为，不弹全局错误，由页面展示空态
        const requestUrl = error.config?.url || '';
        if (error.response?.status === 403 && requestUrl.includes('/api/contest/rank/')) {
          return Promise.reject(error);
        }
        if (error.response?.data?.code === 401) {
          useAuthStore.getState().clearAuth()
          window.location.href = '/login'
        }
        // 处理错误信息（silent 请求跳过全局提示，由调用方自行处理）
        const isSilent = !!error.config?.silent;
        if (!isSilent && error.response?.data?.data) {
          // 如果是对象形式的错误信息，将所有错误信息合并
          const errors = error.response.data.data;
          if (typeof errors === 'object') {
            const errorMessages = Object.values(errors).flat();
            message.error(errorMessages.join(', '));
          } else {
            // 如果是字符串形式的错误信息
            message.error(errors);
          }
        } else if (!isSilent && error.response?.data?.message) {
          // 美化错误信息显示
          let errorMessage = error.response.data.message;

          // 检查是否是Django REST framework的错误格式
          if (typeof errorMessage === 'string' && errorMessage.includes('ErrorDetail')) {
            try {
              // 尝试解析 ErrorDetail(string='...', code='...') 格式
              const match = errorMessage.match(/string='([^']+)'/);
              if (match) {
                errorMessage = match[1];
              } else {
                // 尝试解析其他可能的格式
                const jsonMatch = errorMessage.match(/\{[^}]+\}/);
                if (jsonMatch) {
                  try {
                    const parsed = JSON.parse(jsonMatch[0]);
                    if (parsed.string) {
                      errorMessage = parsed.string;
                    }
                  } catch (parseError) {
                    // 如果解析失败，尝试提取引号中的内容
                    const simpleMatch = errorMessage.match(/'([^']+)'/);
                    if (simpleMatch) {
                      errorMessage = simpleMatch[1];
                    }
                  }
                }
              }
            } catch (parseError) {
              console.warn('解析错误信息失败:', parseError);
              // 如果解析失败，使用默认错误信息
              errorMessage = '操作失败，请稍后重试';
            }
          }

          message.error(errorMessage);
        } else {
          // 默认错误信息
          message.error('操作失败，请稍后重试');
        }
        return Promise.reject(error)
      }
    )
  }

  // HTTP 请求方法
  public async get<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.instance.get<ResponseData<T>>(url, config)
  }

  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.instance.post<ResponseData<T>>(url, data, config)
  }

  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.instance.put<ResponseData<T>>(url, data, config)
  }

  public async delete<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.instance.delete<ResponseData<T>>(url, config)
  }

  public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.instance.patch<ResponseData<T>>(url, data, config)
  }

  // 流式 POST 请求（SSE），直接返回 Response 对象
  public streamPost(url: string, data?: any, signal?: AbortSignal): Promise<Response> {
    const token = useAuthStore.getState().token;
    const base = this.instance.defaults.baseURL || '';
    // 与 axios 内部 combineURLs 一致的拼接方式，避免 // 被解析为协议相对 URL
    const fullUrl = base.replace(/\/+$/, '') + '/' + url.replace(/^\/+/, '');
    return fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Token ${token}`,
      },
      body: data ? JSON.stringify(data) : undefined,
      signal,
    });
  }
}

// 创建并导出单例
const request = new HttpRequest()
export default request 